import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { cn } from "@winche/ui";

/*
 * Charts for the admin (audit 2026-10-10, admin-04/12). The SVG is drawn at
 * the container's real pixel width (ResizeObserver), so axis text stays 11 px
 * on a phone and on a 1440 px screen instead of scaling with a viewBox. One
 * tooltip with a guide line works with mouse, touch (tap or drag) and the
 * keyboard (focus the chart, then ←/→, Home/End, Esc), and a visually hidden
 * table carries the same numbers for screen readers. No dual axes: series
 * with different units go in separate charts with the same X.
 *
 * Color: `--chart-1` (brand) for single-series charts, the categorical
 * `--chart-2…6` by series id, never by rank; red and amber only for
 * thresholds (the reference line).
 */

export interface ChartSeries {
  key: string;
  label: string;
  /** A CSS color, normally `var(--chart-N)`. */
  color: string;
  kind: "bar" | "line";
  /** Bars with `stack` pile up in series order (cohorts). */
  stack?: boolean;
}

/**
 * A horizontal threshold: dashed and labelled. `warning` (amber, the
 * default) for a line that alerts, `danger` (red) for one that stops
 * something.
 */
export interface ChartReference {
  value: number;
  label: string;
  tone?: "warning" | "danger";
}

const REFERENCE_COLORS = {
  warning: { stroke: "var(--warning-solid)", text: "var(--warning-fg)", swatch: "border-warning" },
  danger: { stroke: "var(--danger-solid)", text: "var(--danger-fg)", swatch: "border-danger" },
} as const;

export interface ChartDatum {
  /** Stable key (a date). */
  x: string;
  /** Short axis label ("Oct 3"). */
  xLabel: string;
  /** Tooltip heading; defaults to `xLabel`. */
  tooltipLabel?: string;
  values: Record<string, number | null | undefined>;
  /** Drawn lighter (an incomplete period). */
  muted?: boolean;
}

const AXIS_FONT = 11;
const CHAR_W = 6.4; // ≈ Inter 11 px average advance

function useElementWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(Math.round(el.getBoundingClientRect().width));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function niceStep(raw: number): number {
  if (!(raw > 0)) return 1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return nice * mag;
}

function ticksFor(min: number, max: number, count = 3): { ticks: number[]; lo: number; hi: number } {
  if (max <= min) max = min + 1;
  const step = niceStep((max - min) / count);
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
  return { ticks, lo, hi };
}

function isNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

export function Chart({
  title,
  data,
  series,
  format,
  formatAxis = format,
  height = 180,
  reference,
  invert = false,
  domain,
  showLastValue = true,
  legend,
  className,
}: {
  /** Accessible name (and the hidden table caption). */
  title: string;
  data: readonly ChartDatum[];
  series: readonly ChartSeries[];
  /** Tooltip and last-value format. */
  format: (v: number) => string;
  /** Y-axis tick format (compact). */
  formatAxis?: (v: number) => string;
  height?: number;
  /** Horizontal thresholds (budget, target, cap): dashed and labelled. */
  reference?: ChartReference | readonly ChartReference[];
  /** Lower is better (search position): small values on top. */
  invert?: boolean;
  /** Fixed Y domain instead of the data range. */
  domain?: [number, number];
  /** Writes the last value at the end of each line. */
  showLastValue?: boolean;
  /** Defaults to a legend when there is more than one series. */
  legend?: boolean;
  className?: string;
}) {
  const [boxRef, measured] = useElementWidth<HTMLDivElement>();
  const width = measured || 640;
  const [active, setActive] = useState<number | null>(null);
  const descId = useId();
  const n = data.length;
  const references: readonly ChartReference[] = reference == null ? [] : Array.isArray(reference) ? reference : [reference as ChartReference];

  // Escape anywhere drops the tooltip.
  useEffect(() => {
    if (active == null) return;
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  if (n === 0) return null;

  const stacked = series.filter((s) => s.kind === "bar" && s.stack);
  const groupedBars = series.filter((s) => s.kind === "bar" && !s.stack);
  const lines = series.filter((s) => s.kind === "line");

  // ── Y domain
  const values: number[] = [];
  for (const d of data) {
    let stackSum = 0;
    for (const s of series) {
      const v = d.values[s.key];
      if (!isNum(v)) continue;
      if (s.kind === "bar" && s.stack) stackSum += v;
      else values.push(v);
    }
    if (stacked.length) values.push(stackSum);
  }
  for (const r of references) values.push(r.value);
  const dataMin = values.length ? Math.min(...values) : 0;
  const dataMax = values.length ? Math.max(...values) : 1;
  const hasBars = stacked.length + groupedBars.length > 0;
  const { ticks, lo, hi } = domain
    ? { ticks: ticksFor(domain[0], domain[1]).ticks.filter((t) => t >= domain[0] && t <= domain[1]), lo: domain[0], hi: domain[1] }
    : ticksFor(invert ? Math.max(0, dataMin) : Math.min(0, dataMin), hasBars || !invert ? Math.max(dataMax, 0) : dataMax);

  // ── Layout (real pixels)
  const axisLabels = ticks.map((t) => formatAxis(t));
  const padLeft = Math.ceil(Math.max(...axisLabels.map((l) => l.length)) * CHAR_W) + 10;
  const lastValueLabels = showLastValue
    ? lines.map((s) => {
        for (let i = n - 1; i >= 0; i -= 1) {
          const v = data[i]!.values[s.key];
          if (isNum(v)) return { s, i, v, text: format(v) };
        }
        return null;
      })
    : [];
  const padRight =
    Math.ceil(Math.max(0, ...lastValueLabels.map((l) => (l ? l.text.length * CHAR_W + 10 : 0)))) + 8;
  const pad = { top: 10, right: padRight, bottom: 24, left: padLeft };
  const plotW = Math.max(40, width - pad.left - pad.right);
  const plotH = height - pad.top - pad.bottom;
  const band = plotW / n;
  const cx = (i: number) => pad.left + band * (i + 0.5);
  const y = (v: number) => {
    const t = (v - lo) / (hi - lo || 1);
    return invert ? pad.top + t * plotH : pad.top + (1 - t) * plotH;
  };
  const baseY = invert ? pad.top : y(Math.max(lo, 0));

  // X labels that never collide.
  const maxLabel = Math.max(...data.map((d) => d.xLabel.length)) * CHAR_W + 12;
  const every = Math.max(1, Math.ceil(maxLabel / band));

  const barCount = (stacked.length ? 1 : 0) + groupedBars.length;
  const barW = Math.max(2, Math.min(28, (band * 0.72) / Math.max(1, barCount)));

  const pick = (clientX: number, el: Element) => {
    const rect = el.getBoundingClientRect();
    const xPx = clientX - rect.left;
    const i = Math.floor((xPx - pad.left) / band);
    setActive(Math.max(0, Math.min(n - 1, i)));
  };

  const onPointer = (e: PointerEvent<SVGSVGElement>) => pick(e.clientX, e.currentTarget);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const keys: Record<string, (i: number) => number> = {
      ArrowRight: (i) => Math.min(n - 1, i + 1),
      ArrowLeft: (i) => Math.max(0, i - 1),
      Home: () => 0,
      End: () => n - 1,
    };
    const move = keys[e.key];
    if (move) {
      e.preventDefault();
      setActive((i) => move(i ?? n - 1));
    } else if (e.key === "Escape") {
      setActive(null);
    }
  };

  const linePath = (key: string) => {
    let d = "";
    let pen = false;
    data.forEach((p, i) => {
      const v = p.values[key];
      if (!isNum(v)) {
        pen = false; // a gap, never a fake 0
        return;
      }
      d += `${pen ? "L" : "M"}${cx(i).toFixed(1)},${y(v).toFixed(1)} `;
      pen = true;
    });
    return d.trim();
  };

  const activeDatum = active != null ? data[active] : null;
  const tipLeft = active != null && cx(active) < width / 2;
  const showLegend = legend ?? series.length > 1;
  const describe = (d: ChartDatum) =>
    `${d.tooltipLabel ?? d.xLabel}: ${series
      .map((s) => `${s.label} ${isNum(d.values[s.key]) ? format(d.values[s.key] as number) : "no data"}`)
      .join(", ")}`;

  return (
    <div className={cn("min-w-0", className)}>
      <div
        ref={boxRef}
        className="relative rounded-md outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring"
        tabIndex={0}
        role="group"
        aria-label={`${title}. Use the left and right arrow keys to read each point.`}
        aria-describedby={descId}
        onKeyDown={onKeyDown}
        onFocus={() => setActive((i) => i ?? n - 1)}
        onBlur={() => setActive(null)}
      >
        <svg
          width={width}
          height={height}
          className="block touch-pan-y select-none"
          role="img"
          aria-label={title}
          onPointerMove={onPointer}
          onPointerDown={onPointer}
          onPointerLeave={(e) => e.pointerType === "mouse" && setActive(null)}
        >
          {/* Grid + Y axis */}
          {ticks.map((t, i) => (
            <g key={`t${t}`}>
              <line
                x1={pad.left}
                x2={pad.left + plotW}
                y1={y(t)}
                y2={y(t)}
                stroke="var(--chart-grid)"
                strokeDasharray={t === 0 ? undefined : "3 4"}
              />
              <text x={pad.left - 8} y={y(t) + 4} textAnchor="end" fontSize={AXIS_FONT} fill="var(--chart-axis)">
                {axisLabels[i]}
              </text>
            </g>
          ))}

          {/* Stacked bars */}
          {stacked.length > 0 &&
            data.map((d, i) => {
              let acc = 0;
              const x0 = cx(i) - (barW * barCount) / 2;
              return (
                <g key={`s${d.x}`} opacity={active == null || active === i ? (d.muted ? 0.6 : 1) : 0.45}>
                  {stacked.map((s) => {
                    const v = d.values[s.key];
                    if (!isNum(v) || v <= 0) return null;
                    const top = y(acc + v);
                    const bottom = y(acc);
                    acc += v;
                    return <rect key={s.key} x={x0} y={top} width={barW} height={Math.max(0.5, bottom - top)} fill={s.color} />;
                  })}
                </g>
              );
            })}

          {/* Grouped bars */}
          {groupedBars.map((s, si) =>
            data.map((d, i) => {
              const v = d.values[s.key];
              if (!isNum(v)) return null;
              const x0 = cx(i) - (barW * barCount) / 2 + (si + (stacked.length ? 1 : 0)) * barW;
              const top = Math.min(y(v), baseY);
              const h = Math.max(1, Math.abs(baseY - y(v)));
              return (
                <rect
                  key={`${s.key}-${d.x}`}
                  x={x0}
                  y={top}
                  width={Math.max(1, barW - (barCount > 1 ? 2 : 0))}
                  height={h}
                  rx={Math.min(3, barW / 3)}
                  fill={s.color}
                  opacity={active == null || active === i ? (d.muted ? 0.55 : 1) : 0.45}
                />
              );
            }),
          )}

          {/* Reference line (budget, target) */}
          {references.map((r) => (
            <g key={`${r.label}-${r.value}`}>
              <line
                x1={pad.left}
                x2={pad.left + plotW}
                y1={y(r.value)}
                y2={y(r.value)}
                stroke={REFERENCE_COLORS[r.tone ?? "warning"].stroke}
                strokeWidth={1.5}
                strokeDasharray="6 4"
              />
              <text
                x={pad.left + plotW}
                y={y(r.value) - 6}
                textAnchor="end"
                fontSize={AXIS_FONT}
                fontWeight={600}
                fill={REFERENCE_COLORS[r.tone ?? "warning"].text}
              >
                {r.label}
              </text>
            </g>
          ))}

          {/* Lines */}
          {lines.map((s) => (
            <path
              key={s.key}
              d={linePath(s.key)}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
          {n <= 16 &&
            lines.map((s) =>
              data.map((d, i) =>
                isNum(d.values[s.key]) ? (
                  <circle key={`${s.key}-${d.x}`} cx={cx(i)} cy={y(d.values[s.key] as number)} r={2.5} fill={s.color} />
                ) : null,
              ),
            )}

          {/* Last value, written on the line */}
          {lastValueLabels.map((l) =>
            l ? (
              <text
                key={`lv-${l.s.key}`}
                x={cx(l.i) + 8}
                y={y(l.v) + 4}
                fontSize={AXIS_FONT}
                fontWeight={600}
                fill={l.s.color}
              >
                {l.text}
              </text>
            ) : null,
          )}

          {/* X axis */}
          {data.map((d, i) =>
            i % every === 0 ? (
              <text key={`x${d.x}`} x={cx(i)} y={height - 6} textAnchor="middle" fontSize={AXIS_FONT} fill="var(--chart-axis)">
                {d.xLabel}
              </text>
            ) : null,
          )}

          {/* Guide line + active points */}
          {active != null && (
            <g pointerEvents="none">
              <line
                x1={cx(active)}
                x2={cx(active)}
                y1={pad.top}
                y2={pad.top + plotH}
                stroke="var(--muted-foreground)"
                strokeDasharray="3 3"
              />
              {lines.map((s) => {
                const v = data[active]!.values[s.key];
                return isNum(v) ? (
                  <circle key={`a-${s.key}`} cx={cx(active)} cy={y(v)} r={4} fill={s.color} stroke="var(--card)" strokeWidth={2} />
                ) : null;
              })}
            </g>
          )}
        </svg>

        {activeDatum && (
          <div
            className="pointer-events-none absolute z-10 min-w-36 rounded-md border border-border bg-popover px-3 py-2 text-caption shadow-popover"
            style={
              tipLeft
                ? { left: cx(active!) + 12, top: pad.top }
                : { right: width - cx(active!) + 12, top: pad.top }
            }
          >
            <p className="mb-1 font-medium text-foreground">{activeDatum.tooltipLabel ?? activeDatum.xLabel}</p>
            <ul className="space-y-0.5">
              {series.map((s) => {
                const v = activeDatum.values[s.key];
                return (
                  <li key={s.key} className="flex items-center justify-between gap-4">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Swatch color={s.color} kind={s.kind} />
                      {s.label}
                    </span>
                    <span className="font-medium tabular-nums text-foreground">{isNum(v) ? format(v) : "—"}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <p className="sr-only" aria-live="polite">
          {activeDatum ? describe(activeDatum) : ""}
        </p>
      </div>

      {showLegend && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-caption text-muted-foreground" aria-hidden="true">
          {series.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5">
              <Swatch color={s.color} kind={s.kind} />
              {s.label}
            </span>
          ))}
          {references.map((r) => (
            <span key={`${r.label}-${r.value}`} className="flex items-center gap-1.5">
              <span className={cn("h-0 w-3 border-t-2 border-dashed", REFERENCE_COLORS[r.tone ?? "warning"].swatch)} />
              {r.label}
            </span>
          ))}
        </div>
      )}

      <table id={descId} className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            {series.map((s) => (
              <th key={s.key} scope="col">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.x}>
              <th scope="row">{d.tooltipLabel ?? d.xLabel}</th>
              {series.map((s) => {
                const v = d.values[s.key];
                return <td key={s.key}>{isNum(v) ? format(v) : "no data"}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Swatch({ color, kind }: { color: string; kind: ChartSeries["kind"] }) {
  return kind === "line" ? (
    <span className="inline-block h-0.5 w-3 rounded-full" style={{ backgroundColor: color }} />
  ) : (
    <span className="inline-block size-2 rounded-sm" style={{ backgroundColor: color }} />
  );
}

export interface BarListRow {
  key: string;
  label: ReactNode;
  value: number;
  /** Formatted value. */
  display: string;
  /** Muted text after the value (share, calls). */
  meta?: string;
  /** Plain-text label for the accessible name. */
  title?: string;
}

/**
 * Ranked horizontal bars for one measure (admin-12): one tone, the hovered or
 * focused row in the full brand color — a model keeps the same color however
 * the list is sorted, and no bar looks like an error.
 */
export function BarList({ rows, max: maxRows = 12, moreLabel }: { rows: readonly BarListRow[]; max?: number; moreLabel?: (n: number) => string }) {
  const visible = rows.slice(0, maxRows);
  const max = Math.max(...visible.map((r) => r.value), 0);
  if (visible.length === 0) return null;
  return (
    <div>
      <ul className="space-y-2.5">
        {visible.map((row) => {
          const pct = max > 0 ? (row.value / max) * 100 : 0;
          return (
            <li key={row.key} className="group" title={row.title}>
              <div className="mb-1 flex items-baseline justify-between gap-3 text-label">
                <span className="min-w-0 truncate">{row.label}</span>
                <span className="shrink-0 tabular-nums text-foreground">
                  {row.display}
                  {row.meta ? <span className="ml-2 text-muted-foreground">{row.meta}</span> : null}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-[color:var(--chart-1-muted)] transition-colors duration-150 group-hover:bg-[color:var(--chart-1)]"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
      {rows.length > maxRows && moreLabel ? (
        <p className="mt-3 text-caption text-muted-foreground">{moreLabel(rows.length - maxRows)}</p>
      ) : null}
    </div>
  );
}
